/**
 * Payment Hooks
 * 
 * React Query hooks for payment operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentService } from '../services/paymentService';
import {
  ProductType,
  PaymentIntentCreate,
  SubscriptionCreate,
  SubscriptionCancelRequest,
  EcoCashQuoteRequest,
  EcoCashClaimCreate,
  SubjectAccessPaynowInitiateRequest,
  SubjectAccessStripeIntentRequest,
} from '../types/payments';

// Query keys
export const paymentKeys = {
  all: ['payments'] as const,
  products: () => [...paymentKeys.all, 'products'] as const,
  productsByType: (type?: ProductType) => [...paymentKeys.products(), { type }] as const,
  product: (id: string) => [...paymentKeys.products(), id] as const,
  history: () => [...paymentKeys.all, 'history'] as const,
  subscriptions: () => [...paymentKeys.all, 'subscriptions'] as const,
  activeSubscription: () => [...paymentKeys.subscriptions(), 'active'] as const,
  entitlements: () => [...paymentKeys.all, 'entitlements'] as const,
  capabilities: () => [...paymentKeys.all, 'capabilities'] as const,
  wallet: () => [...paymentKeys.all, 'wallet'] as const,
  subjectAccess: () => [...paymentKeys.all, 'subject-access'] as const,
  subjectAccessCheckout: (checkoutReference: string) =>
    [...paymentKeys.subjectAccess(), 'checkout', checkoutReference] as const,
  ecocashClaims: () => [...paymentKeys.all, 'ecocash', 'claims'] as const,
  ecocashClaim: (claimId: string) => [...paymentKeys.ecocashClaims(), claimId] as const,
};

// ============= PRODUCT HOOKS =============

/**
 * Fetch all products
 */
export function useProducts(productType?: ProductType) {
  return useQuery({
    queryKey: paymentKeys.productsByType(productType),
    queryFn: () => paymentService.getProducts(productType),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch a specific product
 */
export function useProduct(productId: string) {
  return useQuery({
    queryKey: paymentKeys.product(productId),
    queryFn: () => paymentService.getProduct(productId),
    enabled: !!productId,
  });
}

// ============= PAYMENT HOOKS =============

/**
 * Create payment intent mutation
 */
export function useCreatePaymentIntent() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: PaymentIntentCreate) => paymentService.createPaymentIntent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.history() });
    },
  });
}

/**
 * Fetch payment history
 */
export function usePaymentHistory(limit?: number) {
  return useQuery({
    queryKey: [...paymentKeys.history(), { limit }],
    queryFn: () => paymentService.getPaymentHistory(limit),
  });
}

// ============= SUBSCRIPTION HOOKS =============

/**
 * Create subscription mutation
 */
export function useCreateSubscription() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: SubscriptionCreate) => paymentService.createSubscription(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.subscriptions() });
      queryClient.invalidateQueries({ queryKey: paymentKeys.entitlements() });
    },
  });
}

/**
 * Fetch all subscriptions
 */
export function useSubscriptions() {
  return useQuery({
    queryKey: paymentKeys.subscriptions(),
    queryFn: () => paymentService.getSubscriptions(),
  });
}

/**
 * Fetch active subscription
 */
export function useActiveSubscription() {
  return useQuery({
    queryKey: paymentKeys.activeSubscription(),
    queryFn: () => paymentService.getActiveSubscription(),
  });
}

/**
 * Cancel subscription mutation
 */
export function useCancelSubscription() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      subscriptionId,
      data,
    }: {
      subscriptionId: string;
      data: SubscriptionCancelRequest;
    }) => paymentService.cancelSubscription(subscriptionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.subscriptions() });
      queryClient.invalidateQueries({ queryKey: paymentKeys.entitlements() });
    },
  });
}

// ============= SETUP INTENT HOOKS =============

/**
 * Create setup intent mutation
 */
export function useCreateSetupIntent() {
  return useMutation({
    mutationFn: () => paymentService.createSetupIntent(),
  });
}

// ============= CUSTOMER PORTAL HOOKS =============

/**
 * Create customer portal session mutation
 */
export function useCreateCustomerPortal() {
  return useMutation({
    mutationFn: (returnUrl: string) => paymentService.createCustomerPortal(returnUrl),
    onSuccess: (data) => {
      // Redirect to customer portal
      window.location.href = data.portal_url;
    },
  });
}

// ============= ENTITLEMENTS HOOKS =============

/**
 * Fetch user entitlements
 */
export function useEntitlements() {
  return useQuery({
    queryKey: paymentKeys.entitlements(),
    queryFn: () => paymentService.getEntitlements(),
    staleTime: 60 * 1000, // 1 minute
  });
}

export function usePaymentCapabilities(enabled: boolean = true) {
  return useQuery({
    queryKey: paymentKeys.capabilities(),
    queryFn: () => paymentService.getCapabilities(),
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useWalletSnapshot() {
  return useQuery({
    queryKey: paymentKeys.wallet(),
    queryFn: () => paymentService.getWalletSnapshot(),
    staleTime: 30 * 1000,
  });
}

export function useCreateEcoCashQuote() {
  return useMutation({
    mutationFn: (data: EcoCashQuoteRequest) => paymentService.createEcoCashQuote(data),
  });
}

export function useCreateEcoCashClaim() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: EcoCashClaimCreate) => paymentService.createEcoCashClaim(data),
    onSuccess: (claim) => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.wallet() });
      queryClient.invalidateQueries({ queryKey: paymentKeys.entitlements() });
      queryClient.invalidateQueries({ queryKey: paymentKeys.ecocashClaim(claim.id) });
    },
  });
}

export function useCreateSubjectAccessStripeIntent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SubjectAccessStripeIntentRequest) =>
      paymentService.createSubjectAccessStripeIntent(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.wallet() });
      queryClient.invalidateQueries({ queryKey: paymentKeys.entitlements() });
      queryClient.invalidateQueries({
        queryKey: paymentKeys.subjectAccessCheckout(response.checkout.checkout_reference),
      });
    },
  });
}

export function useCreateSubjectAccessPaynowInitiate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SubjectAccessPaynowInitiateRequest) =>
      paymentService.createSubjectAccessPaynowInitiate(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.wallet() });
      queryClient.invalidateQueries({ queryKey: paymentKeys.entitlements() });
      queryClient.invalidateQueries({
        queryKey: paymentKeys.subjectAccessCheckout(response.checkout.checkout_reference),
      });
    },
  });
}

export function useSubjectAccessCheckout(checkoutReference: string, enabled: boolean = true) {
  return useQuery({
    queryKey: paymentKeys.subjectAccessCheckout(checkoutReference),
    queryFn: () => paymentService.getSubjectAccessCheckout(checkoutReference),
    enabled: enabled && !!checkoutReference,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (
        status === 'awaiting_payment' ||
        status === 'awaiting_receipt' ||
        status === 'processing'
      ) {
        return 3000;
      }
      return false;
    },
  });
}

export function useEcoCashClaim(claimId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: paymentKeys.ecocashClaim(claimId),
    queryFn: () => paymentService.getEcoCashClaim(claimId),
    enabled: enabled && !!claimId,
    refetchInterval: (query) => {
      const status = query.state.data?.claim_status;
      if (
        status === 'pending_receipt' ||
        status === 'matched_pending_allocation' ||
        status === 'manual_review'
      ) {
        return 5000;
      }
      return false;
    },
  });
}
